import React from "react";
import { ModalCtx, useReveal, useEditorReveal, useMeta } from "./shared.jsx";
import { Crumbs, RichText } from "./shared-ui.jsx";
import { HlHeading } from "./sections/headings.jsx";
import legal from "../content/legal.json";

// Strony prawne i 404: PolitykaPrywatnosci, RegulaminPage, NotFoundPage.
// Treść edytowalna z ../content/legal.json (CMS /admin/legal — jeden wpis na wszystkie trzy).

const noop = () => {};

// items może być tablicą (publiczna strona / JSON) albo stringiem „linia\nlinia" (edytor Puck).
const asItems = (items) => Array.isArray(items) ? items : String(items || "").split("\n").map(s => s.trim()).filter(Boolean);

const LegalBlock = ({ b }) => {
  if (b.type === "h3") return <h3 className="legal-h" style={{ fontSize: "1em", marginTop: "1.2em" }}><RichText text={b.html} /></h3>;
  if (b.type === "ol") return <ol className="legal-ol">{asItems(b.items).map((it, i) => <li key={i}><RichText text={it} /></li>)}</ol>;
  if (b.type === "ul") return <ul className="legal-ul">{asItems(b.items).map((it, i) => <li key={i}><RichText text={it} /></li>)}</ul>;
  return <p className="legal-p"><RichText text={b.html} /></p>;
};

// Pojedynczy dokument prawny (Polityka / Regulamin).
const LegalDoc = ({ doc, crumbLabel, navigate = noop }) => (
  <div className="container" style={{ paddingTop: 56, paddingBottom: 96 }}>
    <Crumbs items={[{ label: "Home", href: "/" }, { label: crumbLabel }]} navigate={navigate} />
    <h1 style={{ fontFamily: "var(--font-display)", fontSize: "clamp(26px,3.5vw,38px)", fontWeight: 500, letterSpacing: "-0.02em", color: "#fff", margin: "28px 0 6px" }}>{doc.title}</h1>
    <p style={{ fontSize: 13, color: "var(--fg-dim)", margin: "0 0 48px", fontFamily: "var(--font-display)" }}>{doc.updated}</p>
    <div className="legal-doc">
      {(doc.sections || []).map((sec, i) => (
        <div key={i} className="legal-section">
          <h2 className="legal-h">{sec.h}</h2>
          {(sec.blocks || []).map((b, j) => <LegalBlock key={j} b={b} />)}
        </div>
      ))}
    </div>
  </div>
);

// Treść 404 (prezentacyjna).
const NotFoundContent = ({ content: nf, route = "/przykładowa-strona", navigate = noop }) => {
  const popular = nf.popular || [];
  return (
    <div className="nf-wrap">
      <section className="page-hero bg-tiles container nf-hero">
        <div className="nf-inner">
          <span className="nf-code rv">{nf.code}</span>
          <h1 className="h-display rv nf-h">
            <HlHeading before={nf.headingBefore} highlight={nf.headingHighlight} after={nf.headingAfter} />
          </h1>
          <p className="lead rv nf-lead">
            {nf.leadBefore}<code className="nf-path">{route}</code>{nf.leadAfter}
          </p>
          <div className="hero-ctas rv nf-ctas">
            <button className="lc-btn lc-btn-ghost lc-btn-lg" onClick={() => navigate("/")}>
              {nf.ctaPrimary} <span className="lc-btn-arr">↗</span>
            </button>
            <button className="lc-btn lc-btn-link lc-btn-lg" onClick={() => navigate("/kontakt")}>
              {nf.ctaSecondary} <span className="lc-btn-arr">↗</span>
            </button>
          </div>
          <div className="nf-quick rv">
            <span className="nf-quick-l">{nf.quickLabel}</span>
            <div className="nf-quick-list">
              {popular.map(p => (
                <button key={p.href} className="nf-quick-item" onClick={() => navigate(p.href)}>
                  {p.label} <span className="nf-quick-arr">↗</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ─── Publiczne strony ───────────────────────────────────────────────────────
const PolitykaPrywatnosci = ({ navigate }) => {
  useMeta({ title: legal.polityka.meta.title, description: legal.polityka.meta.description });
  return <LegalDoc doc={legal.polityka} crumbLabel="Polityka prywatności" navigate={navigate} />;
};

const RegulaminPage = ({ navigate }) => {
  useMeta({ title: legal.regulamin.meta.title, description: legal.regulamin.meta.description });
  return <LegalDoc doc={legal.regulamin} crumbLabel="Regulamin" navigate={navigate} />;
};

const NotFoundPage = ({ navigate, route }) => {
  const ref = React.useRef(null); useReveal(ref);
  React.useContext(ModalCtx);
  useMeta({ title: legal.notFound.meta.title, description: legal.notFound.meta.description });
  return <div ref={ref}><NotFoundContent content={legal.notFound} route={route} navigate={navigate} /></div>;
};

// ─── Podgląd dla edytora (wszystkie trzy dokumenty stackowane) ──────────────
export const LegalAllBody = ({ content: c = legal, navigate = noop, editor = false }) => {
  const ref = React.useRef(null);
  useReveal(ref);
  useEditorReveal(ref, editor);
  return (
    <div ref={ref}>
      <LegalDoc doc={c.polityka} crumbLabel="Polityka prywatności" navigate={navigate} />
      <LegalDoc doc={c.regulamin} crumbLabel="Regulamin" navigate={navigate} />
      <NotFoundContent content={c.notFound} navigate={navigate} />
    </div>
  );
};

export { PolitykaPrywatnosci, RegulaminPage, NotFoundPage };
